import {
  Button,
  Icon,
  PageContainer,
  RouteButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiChevronDown, mdiChevronUp, mdiTrashCan } from "@mdi/js";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Alert,
  Box,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiErrors } from "../../../../@types/components/organisms/ApiErrors";
import {
  removeCallOffItemFromPurchaseOrderGroup,
  retryCreatePurchaseOrderByCallOffContract,
  useShowCreatePurchaseOrderByCallOffContract,
} from "../../../../__generated__/orval/endpoints/create-purchase-order-by-call-off-contract/create-purchase-order-by-call-off-contract";
import { EntitiesCreatePurchaseOrderByCallOffContractMemberMain } from "../../../../__generated__/orval/models/entitiesCreatePurchaseOrderByCallOffContractMemberMain.zod";
import { ApiErrorsTable } from "../../../../components/organisms/ApiErrorsTable";
import { useAuth } from "../../../../hooks/useAuth";
import { getMicrosoftToken } from "../../../../services/getMicrosoftToken";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";
import { executionSearchSchema } from "../index";

const formatPrice = (value: string | null | undefined, locale: string) => {
  if (!value) return "-";

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(Number(value));
};

const ExecutionDetail = () => {
  const { createPurchaseOrderByCallOffContractId } = Route.useParams();
  const search = Route.useSearch();
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState("items");
  const [itemToRemove, setItemToRemove] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(false);
  const [expandedExecution, setExpandedExecution] = useState<string | null>(
    null,
  );
  const { data, isPending, isError, refetch } =
    useShowCreatePurchaseOrderByCallOffContract(
      createPurchaseOrderByCallOffContractId,
      {
        query: {
          refetchInterval: (query) => {
            const batch =
              EntitiesCreatePurchaseOrderByCallOffContractMemberMain.safeParse(
                query.state.data,
              );
            return batch.success &&
              (batch.data.execution_status === "processing" ||
                batch.data.call_off_items.some(
                  (item) => item.purchase_order_link_pending,
                ))
              ? 5000
              : false;
          },
        },
      },
    );
  const parsed =
    EntitiesCreatePurchaseOrderByCallOffContractMemberMain.safeParse(data);
  const canGenerate = user?.roles_names.includes(
    "Call-Off Contract - Generate Purchase Order",
  );
  const canChange = Boolean(
    canGenerate &&
      parsed.success &&
      parsed.data.execution_status === "failed" &&
      parsed.data.can_retry,
  );

  const handleRetry = async () => {
    setActionError(false);
    setActionLoading(true);
    try {
      if (!(await getMicrosoftToken("procurement"))) return;
      await retryCreatePurchaseOrderByCallOffContract(
        createPurchaseOrderByCallOffContractId,
      );
      await refetch();
    } catch {
      setActionError(true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!itemToRemove) return;
    setActionError(false);
    setActionLoading(true);
    try {
      await removeCallOffItemFromPurchaseOrderGroup(
        createPurchaseOrderByCallOffContractId,
        itemToRemove,
      );
      setItemToRemove(null);
      await refetch();
    } catch {
      setActionError(true);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <PageContainer title={t("createPurchaseOrderByQuotation.title")}>
      <RouteButtonLink
        search={search}
        to="/create_purchase_order_by_call_off_contracts"
        variant="outlined"
      >
        {t("common.back")}
      </RouteButtonLink>
      {!!actionError && (
        <Alert severity="error">{t("callOffContract.actionError")}</Alert>
      )}
      {isError || (!isPending && !parsed.success) ? (
        <Alert severity="error">{t("callOffContract.loadError")}</Alert>
      ) : isPending || !parsed.success ? (
        <Skeleton height={120} />
      ) : (
        <TabContext value={tab}>
          {canChange && (
            <Box sx={{ mb: 2 }}>
              <Button
                disabled={actionLoading}
                onClick={handleRetry}
                variant="outlined"
              >
                {t("createPurchaseOrderByQuotation.runAgain")}
              </Button>
            </Box>
          )}
          {parsed.data.execution_status === "cancelled" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("callOffContract.archivedGroup")}
            </Alert>
          )}
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              onChange={(_event, value: string) => setTab(value)}
              value={tab}
            >
              <Tab
                label={t("callOffContract.previewItemsCount", {
                  count: parsed.data.call_off_items.length,
                })}
                value="items"
              />
              <Tab
                label={t("createPurchaseOrderByQuotation.executions")}
                value="executions"
              />
            </Tabs>
          </Box>
          <TabPanel value="items">
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 1400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("callOffContract.referenceNumber")}
                    </TableCell>
                    <TableCell>{t("callOffContract.contract")}</TableCell>
                    <TableCell>{t("callOffContract.prNumber")}</TableCell>
                    <TableCell>{t("callOffContract.trackingNumber")}</TableCell>
                    <TableCell>{t("callOffContract.poNumber")}</TableCell>
                    <TableCell>{t("callOffContract.description")}</TableCell>
                    <TableCell>{t("callOffContract.supplier")}</TableCell>
                    <TableCell>{t("callOffContract.requester")}</TableCell>
                    <TableCell>{t("callOffContract.plant")}</TableCell>
                    <TableCell>{t("common.quantity")}</TableCell>
                    <TableCell>{t("callOffContract.itemType")}</TableCell>
                    <TableCell>{t("callOffContract.unitPrice")}</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.data.call_off_items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.reference_number}</TableCell>
                      <TableCell>
                        {item.purchase_contract} /{" "}
                        {item.purchase_contract_item || "-"}
                      </TableCell>
                      <TableCell>
                        {item.pr_number} / {item.item_number}
                      </TableCell>
                      <TableCell>{item.tracking_number || "-"}</TableCell>
                      <TableCell>
                        {item.purchase_order_number || "-"} /{" "}
                        {item.purchase_order_item_number ?? "-"}
                      </TableCell>
                      <TableCell>{item.description || "-"}</TableCell>
                      <TableCell>{item.supplier || "-"}</TableCell>
                      <TableCell>{item.requester || "-"}</TableCell>
                      <TableCell>{item.plant || "-"}</TableCell>
                      <TableCell>{item.open_quantity ?? "-"}</TableCell>
                      <TableCell>
                        {item.item_category === "9"
                          ? t("common.service")
                          : item.item_category === "0"
                            ? t("common.material")
                            : "-"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {item.unit_price != null && item.currency
                          ? `${item.currency} `
                          : ""}
                        {formatPrice(item.unit_price, i18n.language)}
                      </TableCell>
                      <TableCell align="right">
                        {canChange && !!item.removable && (
                          <Tooltip title={t("remove")}>
                            <IconButton
                              aria-label={t("remove")}
                              onClick={() => setItemToRemove(item.id)}
                            >
                              <Icon icon={mdiTrashCan} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <TabPanel value="executions">
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>
                      {t("createPurchaseOrderByQuotationExecution.createdAt")}
                    </TableCell>
                    <TableCell>
                      {t("createPurchaseOrderByQuotationExecution.updatedAt")}
                    </TableCell>
                    <TableCell>
                      {t("createPurchaseOrderByQuotationExecution.completed")}
                    </TableCell>
                    <TableCell>
                      {t("createPurchaseOrderByQuotationExecution.success")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.data.create_purchase_order_by_call_off_contract_executions.map(
                    (execution) => (
                      <Fragment key={execution.id}>
                        <TableRow>
                          <TableCell>
                            {Object.keys(execution.api_errors).length > 0 && (
                              <IconButton
                                onClick={() =>
                                  setExpandedExecution(
                                    expandedExecution === execution.id
                                      ? null
                                      : execution.id,
                                  )
                                }
                                size="small"
                              >
                                <Icon
                                  icon={
                                    expandedExecution === execution.id
                                      ? mdiChevronUp
                                      : mdiChevronDown
                                  }
                                />
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>
                            {t(
                              "intlDateTime",
                              i18nextParseToDate(execution.created_at),
                            )}
                          </TableCell>
                          <TableCell>
                            {t(
                              "intlDateTime",
                              i18nextParseToDate(execution.updated_at),
                            )}
                          </TableCell>
                          <TableCell>
                            {execution.completed ? t("yes") : t("no")}
                          </TableCell>
                          <TableCell>
                            {execution.success ? t("yes") : t("no")}
                          </TableCell>
                        </TableRow>
                        {Object.keys(execution.api_errors).length > 0 && (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ p: 0 }}>
                              <Collapse
                                in={expandedExecution === execution.id}
                                unmountOnExit
                              >
                                <ApiErrorsTable
                                  apiErrors={execution.api_errors as ApiErrors}
                                />
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ),
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </TabContext>
      )}
      <Dialog
        onClose={() => setItemToRemove(null)}
        open={itemToRemove !== null}
      >
        <DialogTitle>{t("remove")}</DialogTitle>
        <DialogContent>
          <Typography>{t("callOffContract.removeFromGroupMessage")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={actionLoading}
            onClick={() => setItemToRemove(null)}
            variant="outlined"
          >
            {t("common.cancel")}
          </Button>
          <Button
            disabled={actionLoading}
            onClick={handleRemove}
            variant="contained"
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/create_purchase_order_by_call_off_contracts/$createPurchaseOrderByCallOffContractId/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: executionSearchSchema,
  search: {
    middlewares: [stripSearchParams({ itemsPerPage: 25, page: 1 })],
  },
  component: ExecutionDetail,
});
